import React, { useState, useEffect } from "react";
import ChevronRight from "../Icons/ChevronRight";

interface EventCalendarProps {
  onDateSelect?: (date: Date) => void;
  selectedDate?: Date;
  events?: { date: Date; type: "wsg" | "industry"; eventId?: string }[];
  resetToToday?: boolean;
}

const EventCalendar: React.FC<EventCalendarProps> = ({
  onDateSelect,
  selectedDate,
  events = [],
  resetToToday = false
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const firstDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();
  
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  const dayNames = ["S", "M", "T", "W", "T", "F", "S"];
  
  const handleDateClick = (day: number) => {
    const clickedDate = new Date(year, month, day);
    onDateSelect?.(clickedDate);
  };
  
  const hasEvent = (day: number) => {
    const date = new Date(year, month, day);
    return events.some(event => 
      event.date.toDateString() === date.toDateString()
    );
  };
  
  const getEventType = (day: number) => {
    const date = new Date(year, month, day);
    const event = events.find(event => 
      event.date.toDateString() === date.toDateString()
    );
    return event?.type;
  };
  
  const isToday = (day: number) => {
    const date = new Date(year, month, day);
    return date.toDateString() === today.toDateString();
  };
  
  const isSelected = (day: number) => {
    if (!selectedDate) {
      return false;
    }
    const date = new Date(year, month, day);
    return date.toDateString() === selectedDate.toDateString();
  };
  
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  useEffect(() => {
    if (resetToToday) {
      setCurrentDate(new Date());
    }
  }, [resetToToday]);
  
  const renderDays = () => {
    const days = [];
    
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-6 w-6"></div>
      );
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const hasEventOnDay = hasEvent(day);
      const eventType = getEventType(day);
      const isTodayDate = isToday(day);
      const isSelectedDate = isSelected(day);
      
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`
            h-6 w-full flex items-center justify-center text-center text-base leading-6 font-semibold relative text-gray-font  
            ${hasEventOnDay ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}
            ${!isTodayDate && !isSelectedDate && !hasEventOnDay ? 'hover:bg-gray-100' : ''}
          `}
        >
          <span className={`
            w-6 h-6 flex items-center justify-center rounded-full
            ${isTodayDate && !hasEventOnDay && !isSelectedDate ? 'bg-blue-100 text-blue-600' : ''}
            ${isSelectedDate && !hasEventOnDay ? 'bg-transparent border border-primary text-primary' : ''}
            ${hasEventOnDay ? (eventType !== 'wsg' ? 'bg-transparent border border-primary text-primary' : 'bg-transparent border border-blue-500 text-blue-500') : ''}
          `}>
            {day}
          </span>
        </button>
      );
    }
    
    return days;
  };
  
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={goToPreviousMonth}
          className="p-1 hover:bg-gray-100 rounded text-primary"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
        
        <h3 className="text-xl font-bold text-primary leading-7">
          {monthNames[month]} {year}
        </h3>
        
        <button
          onClick={goToNextMonth}
          className="p-1 hover:bg-gray-100 rounded text-primary"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map((day, index) => (
          <div key={index} className="h-6 flex items-center justify-center text-center text-lg leading-normal font-bold text-gray-font">
            {day}
          </div>
        ))}
      </div>
      
        <div className="grid grid-cols-7 gap-1">
          {renderDays()}
        </div>
    </div>
  );
};

export default EventCalendar;
